import './style.scss'
import React, { useContext, useEffect, useMemo, useState } from 'react'
import {
    FiPlus, FiDownload, FiX,
    FiShield, FiShieldOff, FiTrash2, FiEdit2,
} from 'react-icons/fi'
import utility from 'src/utils/utility'
import {
    Box, Flex, Text, tokens,
    Button, Card, BackLink,
    TextField, SearchField, Checkbox,
    InstitutionTable, TableColumn, Toolbar,
} from 'src/componentv2'
import { fontBold, fontMedium } from 'src/model/v2/interface/constants'
import { PAGE_PADDING_X } from 'src/model/v2/interface/constants'
import CustomerApiContext from 'src/provider/API/v2/call-service/Customer/index'
import { permissionLabel, sortPermissions } from './permissions'
import type { Permission, Role } from './types'

// ── Empty state ─────────────────────────────────────────────────────────
function EmptyState({ onNew }: { onNew: () => void }) {
    return (
        <Flex direction="column" items="center" justify="center" style={{ padding: '6.4rem 2.4rem', textAlign: 'center' }}>
            <Box
                width="6.4" height="6.4" radius="999"
                bgColor={tokens.pageBg}
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '2.4rem' }}
            >
                <FiShieldOff size={28} color={tokens.textMuted} />
            </Box>
            <Text fontSize="1.6" style={fontMedium} color={tokens.textDark}>
                No roles created yet
            </Text>
            <Text
                topMargin="1"
                fontSize="1.4"
                color={tokens.textMuted}
                style={{ maxWidth: '32rem', textAlign: 'center', lineHeight: 1.5 }}
            >
                Create your first role to start assigning permissions to team members
            </Text>
            <Button onClick={onNew} topMargin="3.2">
                <FiPlus size={16} style={{ marginRight: '0.6rem' }} />
                New Role
            </Button>
        </Flex>
    )
}

// ── Create role screen ──────────────────────────────────────────────────
// NOTE: there's no update/delete endpoint yet (only CreateRole + Roles list),
// so this screen only supports creating a new role. Editing an existing role
// is view-only for now — see the disabled Save button below.
function CreateEditRoleScreen({
                                  permissions, editRole, onSave, onBack,
                              }: {
    permissions: Permission[]
    editRole?: Role | null
    onSave: (name: string, description: string, permissionIds: number[]) => void
    onBack: () => void
}) {
    const isEditing = !!editRole
    const [name, setName] = useState(editRole?.name ?? '')
    const [description, setDescription] = useState(editRole?.description ?? '')
    const [selected, setSelected] = useState<Set<number>>(new Set(editRole?.permissions.map((p) => p.id) ?? []))
    const [search, setSearch] = useState('')
    const [errors, setErrors] = useState<{ name?: string; description?: string; permissions?: string }>({})

    const sorted = useMemo(() => sortPermissions(permissions), [permissions])
    const filtered = useMemo(() => {
        if (!search.trim()) return sorted
        const q = search.toLowerCase()
        return sorted.filter((p) => permissionLabel(p).toLowerCase().includes(q))
    }, [sorted, search])

    const handleToggle = (id: number, checked: boolean) => {
        setSelected((prev) => {
            const next = new Set(prev)
            checked ? next.add(id) : next.delete(id)
            return next
        })
        setErrors((e) => ({ ...e, permissions: undefined }))
    }

    const handleSelectAll = () => {
        setSelected(new Set(permissions.map((p) => p.id)))
        setErrors((e) => ({ ...e, permissions: undefined }))
    }

    const handleClearAll = () => setSelected(new Set())

    const validate = () => {
        const errs: typeof errors = {}
        if (!name.trim()) errs.name = 'Role name is required.'
        if (!description.trim()) errs.description = 'Role description is required.'
        if (selected.size === 0) errs.permissions = 'Please select at least one permission.'
        setErrors(errs)
        return Object.keys(errs).length === 0
    }

    const handleSave = () => {
        if (!validate()) return
        onSave(name.trim(), description.trim(), Array.from(selected))
    }

    return (
        <div style={{ padding: `2.4rem ${PAGE_PADDING_X}` }}>
            <Flex items="center" gap="1.2">
                <BackLink onClick={onBack} color="#000000">{''}</BackLink>
                <Text fontSize="2.4" style={fontBold} color={tokens.textDark}>
                    {isEditing ? 'View Role' : 'Create New Role'}
                </Text>
            </Flex>

            <div className="rp-page__grid">
                <div className="rp-page__grid-side">
                    <Card padding="2rem" radius="1.2" borderColor={tokens.border} shadow={tokens.shadowCard}>
                        <Text fontSize="1.6" style={fontMedium} color={tokens.textDark}>Role Details</Text>

                        <TextField
                            label="Role Name"
                            placeholder="e.g. Compliance Officer"
                            value={name}
                            onChange={(v: string) => { setName(v); setErrors((e) => ({ ...e, name: undefined })) }}
                            error={errors.name}
                            disabled={isEditing}
                            required
                        />

                        <Box topMargin="1.6" className="rp-page__description-field">
                            <TextField
                                label="Role Description"
                                placeholder="Briefly describe what this role can do..."
                                value={description}
                                onChange={(v: string) => { setDescription(v); setErrors((e) => ({ ...e, description: undefined })) }}
                                multiline
                                rows={4}
                                error={errors.description}
                                disabled={isEditing}
                                required
                            />
                        </Box>
                    </Card>

                    <Card padding="2rem" radius="1.2" borderColor={tokens.border} shadow={tokens.shadowCard} topMargin="1.6">
                        <Flex justify="space-between" items="center">
                            <Text fontSize="1.4" style={fontMedium} color={tokens.textDark}>Permissions Summary</Text>
                            <span
                                className="rp-page__count-pill"
                                style={{
                                    background: selected.size > 0 ? tokens.primarySoft : tokens.pageBg,
                                    color: selected.size > 0 ? tokens.primary : tokens.textMuted,
                                }}
                            >
                                {selected.size} / {permissions.length}
                            </span>
                        </Flex>
                        {errors.permissions && (
                            <Text topMargin="0.6" fontSize="1.3" color="#D92D20">
                                {errors.permissions}
                            </Text>
                        )}
                    </Card>

                    <Flex direction="column" gap="0.8" topMargin="1.6">
                        {isEditing ? (
                            <Button disabled style={{ width: '100%' }} title="Editing roles isn't supported by the API yet">
                                Save Changes
                            </Button>
                        ) : (
                            <Button onClick={handleSave} style={{ width: '100%' }}>
                                Create Role
                            </Button>
                        )}
                        <Button variant="outline" onClick={onBack} style={{ width: '100%' }}>
                            {isEditing ? 'Back' : 'Cancel'}
                        </Button>
                    </Flex>
                </div>

                <div className="rp-page__grid-main">
                    <Flex justify="space-between" items="center" wrap="wrap" gap="1.2">
                        <Text fontSize="1.6" style={fontMedium} color={tokens.textDark}>
                            {isEditing ? 'Assigned Permissions' : 'Assign Permissions'}
                        </Text>
                        {!isEditing && (
                            <Flex items="center" gap="0.8">
                                <Button variant="ghost" onClick={handleClearAll}>
                                    <FiX size={14} style={{ marginRight: '0.4rem' }} />
                                    Clear all
                                </Button>
                                <Button variant="outline" onClick={handleSelectAll}>
                                    <FiShield size={14} style={{ marginRight: '0.4rem' }} />
                                    Select all
                                </Button>
                            </Flex>
                        )}
                    </Flex>

                    <Box topMargin="1.2">
                        <SearchField width="100%" value={search} onChange={setSearch} placeholder="Search permissions" />
                    </Box>

                    <Card padding="0" radius="1.2" borderColor={tokens.border} shadow={tokens.shadowCard} topMargin="1.2" className="rp-page__perm-card">
                        {filtered.length === 0 ? (
                            <Flex direction="column" items="center" gap="0.8" style={{ padding: '3.2rem 0' }}>
                                <Text fontSize="1.4" color={tokens.textMuted}>No permissions match your search.</Text>
                            </Flex>
                        ) : (
                            filtered.map((permission) => {
                                const isChecked = selected.has(permission.id)
                                return (
                                    <Checkbox
                                        key={permission.id}
                                        checked={isChecked}
                                        onChange={(checked) => handleToggle(permission.id, checked)}
                                        disabled={isEditing}
                                        className="rp-page__perm-row"
                                        label={
                                            <span className={`rp-page__perm-label${isChecked ? ' is-checked' : ''}`}>
                    {permissionLabel(permission)}
                </span>
                                        }
                                    />
                                )
                            })
                        )}
                    </Card>
                </div>
            </div>
        </div>
    )
}

// ── List view ───────────────────────────────────────────────────────────
function RolesListView({
                           roles, loading, onNew, onView,
                       }: {
    roles: Role[]
    loading: boolean
    onNew: () => void
    onView: (role: Role) => void
}) {
    const [search, setSearch] = useState('')

    const filtered = useMemo(() => {
        if (!search.trim()) return roles
        const q = search.toLowerCase()
        return roles.filter((r) => (r.name ?? '').toLowerCase().includes(q) || (r.description ?? '').toLowerCase().includes(q))
    }, [roles, search])

    const columns: TableColumn<Role>[] = [
        {
            key: 'name',
            title: 'Role Name',
            render: (row) => (
                <Flex items="center" gap="1">
                    <Box
                        width="2.8" height="2.8" radius="0.8"
                        bgColor={tokens.primarySoft}
                        style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}
                    >
                        <FiShield size={14} color={tokens.primary} />
                    </Box>
                    <Text fontSize="1.4" style={fontMedium} color={tokens.textDark}>{row.name || '—'}</Text>
                </Flex>
            ),
        },
        {
            key: 'description',
            title: 'Role Description',
            render: (row) => (
                <Text fontSize="1.4" color={tokens.textMuted} className="rp-page__truncate">
                    {row.description || '—'}
                </Text>
            ),
        },
        {
            key: 'permissions',
            title: 'No. of Permissions',
            render: (row) => (
                <span className="rp-page__count-pill" style={{ background: tokens.primarySoft, color: tokens.primary }}>
                    {row.permissions.length}
                </span>
            ),
        },
        {
            key: 'actions',
            title: '',
            width: '10rem',
            align: 'right',
            render: (row) => (
                <Flex justify="flex-end" gap="0.4">
                    <button type="button" className="rp-page__icon-btn" aria-label={`View ${row.name}`} onClick={() => onView(row)}>
                        <FiEdit2 size={15} color={tokens.textMuted} />
                    </button>
                    {/* No delete endpoint documented yet — disabled until the API supports it. */}
                    <button
                        type="button"
                        className="rp-page__icon-btn"
                        aria-label="Delete role (not yet supported)"
                        disabled
                        title="Deleting roles isn't supported by the API yet"
                    >
                        <FiTrash2 size={15} color={tokens.textMuted} />
                    </button>
                </Flex>
            ),
        },
    ]

    return (
        <div style={{ padding: `2.4rem ${PAGE_PADDING_X}` }}>
            <Flex justify="space-between" items="center">
                <Text fontSize="2.4" style={fontBold} color={tokens.textDark}>Roles &amp; Permissions</Text>
                <Button onClick={onNew}>
                    <FiPlus size={16} style={{ marginRight: '0.6rem' }} />
                    New Role
                </Button>
            </Flex>

            <Box topMargin="2.4" bgColor={tokens.white} borderColor={tokens.border} radius="1.2" shadow={tokens.shadowCard} style={{ overflow: 'hidden' }}>
                <Box style={{ padding: '0em 2rem', borderBottom: `1px solid ${tokens.border}` }}>
                    <Toolbar
                        left={(
                            <>
                                <SearchField width="32rem" value={search} onChange={setSearch} placeholder="Search roles..." />
                                {search && (
                                    <Button variant="ghost" onClick={() => setSearch('')}>
                                        <FiX size={14} style={{ marginRight: '0.4rem' }} />
                                        Clear
                                    </Button>
                                )}
                            </>
                        )}
                        right={(
                            <Button variant="outline">
                                <FiDownload size={14} style={{ marginRight: '0.6rem' }} />
                                Download
                            </Button>
                        )}
                    />
                </Box>

                {loading ? (
                    <Flex justify="center" style={{ padding: '4.8rem 0' }}>
                        <Text fontSize="1.4" color={tokens.textMuted}>Loading roles...</Text>
                    </Flex>
                ) : roles.length === 0 ? (
                    <EmptyState onNew={onNew} />
                ) : (
                    <InstitutionTable
                        columns={columns}
                        data={filtered}
                        emptyText="No roles match your search."
                        pageSize={8}
                    />
                )}
            </Box>
        </div>
    )
}

// ── Root component ──────────────────────────────────────────────────────
type Screen = 'list' | 'create' | 'view'

const RolesAndPermissionPage = () => {
    const { permissions, getPermissions, roles, getRoles, createRole } = useContext(CustomerApiContext)
    const [screen, setScreen] = useState<Screen>('list')
    const [viewTarget, setViewTarget] = useState<Role | null>(null)
    const [loadingRoles, setLoadingRoles] = useState(true)

    useEffect(() => {
        (async () => {
            await Promise.all([getPermissions(), getRoles()])
            setLoadingRoles(false)
        })()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const handleNew = () => setScreen('create')
    const handleView = (role: Role) => { setViewTarget(role); setScreen('view') }
    const handleBack = () => { setViewTarget(null); setScreen('list') }

    const handleSave = async (name: string, description: string, permissionIds: number[]) => {
        const ok = await createRole({ name, description, permissions: permissionIds })
        if (ok) {
            await getRoles()
            setScreen('list')
        }
    }

    return (
        <div className="rp-page">
            {screen === 'list' && (
                <RolesListView roles={roles} loading={loadingRoles} onNew={handleNew} onView={handleView} />
            )}
            {(screen === 'create' || screen === 'view') && (
                <CreateEditRoleScreen
                    permissions={permissions}
                    editRole={screen === 'view' ? viewTarget : null}
                    onSave={handleSave}
                    onBack={handleBack}
                />
            )}
        </div>
    )
}

export default utility.routeData(
    'roles-permissions',
    'Roles & Permissions',
    <RolesAndPermissionPage />,
)